import React, { useState, useContext } from "react";
import styled from "styled-components";
import { db, storage } from "./firebase/firebaseConfig";
import { addDoc, collection } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { Save, Plus, Trash, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "./context/UserContext";

// ==== Styled Components ====
const PageContainer = styled.div`
  background: #121212;
  color: #fff;
  min-height: 100vh;
  padding: 30px;
  font-family: "Roboto", sans-serif;
`;

const Card = styled.div`
  background: #1e1e2f;
  padding: 20px;
  border-radius: 12px;
  max-width: 900px;
  margin: auto;
  position: relative;
`;

const Title = styled.h2`
  color: #00d4ff;
  margin-bottom: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin: 10px 0;
  border-radius: 8px;
  border: none;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 10px;
  margin: 10px 0;
  border-radius: 8px;
  border: none;
  resize: vertical;
`;

const Label = styled.label`
  display: block;
  margin-top: 15px;
  font-weight: bold;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #00d4ff;
  color: #121212;
  border: none;
  padding: 12px 18px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  margin-top: 20px;
  &:hover {
    background: #00bcd4;
  }
`;

const ChapterList = styled.div`
  margin-top: 15px;
  background: #272743;
  padding: 15px;
  border-radius: 8px;
`;

const ChapterItem = styled.div`
  display: flex;
  justify-content: space-between;
  background: #1e1e2f;
  padding: 10px;
  margin-bottom: 8px;
  border-radius: 6px;
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(18, 18, 18, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  z-index: 10;
`;

const AddCoursePage = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [course, setCourse] = useState({
    title: "",
    description: "",
    price: "",
    discount: "",
    category: "",
    tutor: "",
    image: null,
    demoVideo: null,
    comingDate: "", // 👈 new field
  });

  const [chapters, setChapters] = useState([]);
  const [newChapter, setNewChapter] = useState({
    title: "",
    duration: "",
    video: null,
  });

  const [loading, setLoading] = useState(false);

  const finalPrice =
    course.price && course.discount
      ? (course.price - (course.price * course.discount) / 100).toFixed(2)
      : course.price;

  // Add Chapter
  const handleAddChapter = () => {
    if (!newChapter.title) {
      alert("Please enter chapter title");
      return;
    }
    setChapters([...chapters, newChapter]);
    setNewChapter({ title: "", duration: "", video: null });
  };

  const handleRemoveChapter = (index) => {
    setChapters(chapters.filter((_, i) => i !== index));
  };

  // Upload Course
  const handleUpload = async () => {
    try {
      if (!user?.id) {
        alert("User not logged in ❌");
        return;
      }

      if (!course.title || !course.price) {
        alert("Please fill all required fields");
        return;
      }

      setLoading(true);

      // Upload Image
      let imageUrl = "";
      if (course.image) {
        const imgRef = ref(
          storage,
          `courses/images/${Date.now()}_${course.image.name}`
        );
        await uploadBytesResumable(imgRef, course.image);
        imageUrl = await getDownloadURL(imgRef);
      }

      // Upload Demo Video
      let demoVideoUrl = "";
      if (course.demoVideo) {
        const videoRef = ref(
          storage,
          `courses/demo/${Date.now()}_${course.demoVideo.name}`
        );
        await uploadBytesResumable(videoRef, course.demoVideo);
        demoVideoUrl = await getDownloadURL(videoRef);
      }

      // Upload Chapter Videos
      const uploadedChapters = [];
      for (const chapter of chapters) {
        let videoUrl = "";
        if (chapter.video) {
          const chapRef = ref(
            storage,
            `courses/chapters/${Date.now()}_${chapter.video.name}`
          );
          await uploadBytesResumable(chapRef, chapter.video);
          videoUrl = await getDownloadURL(chapRef);
        }
        uploadedChapters.push({
          title: chapter.title,
          duration: chapter.duration || null, // 👈 optional
          videoUrl,
        });
      }

      // Check course status
      const today = new Date();
      const courseDate = new Date(course.comingDate);
      const status = courseDate > today ? "coming_soon" : "live";

      // Save Course in Firestore
      await addDoc(collection(db, "courses"), {
        title: course.title,
        description: course.description,
        price: parseFloat(course.price),
        discount: parseFloat(course.discount) || 0,
        finalPrice: parseFloat(finalPrice) || parseFloat(course.price),
        category: course.category,
        tutor: course.tutor,
        imageUrl,
        demoVideoUrl,
        createdAt: new Date(),
        comingDate: course.comingDate || null,
        status, // 👈 added
        createdBy: user?.id || null,
        chapters: uploadedChapters,
      });

      alert("Course Added 🚀");
      navigate("/admin-panel");
    } catch (err) {
      console.error(err);
      alert("Error uploading course ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Card>
        {loading && (
          <LoaderOverlay>
            <Loader2 size={40} className="spin" />
          </LoaderOverlay>
        )}

        <Title>Add New Course</Title>

        <Label>Course Title</Label>
        <Input
          type="text"
          value={course.title}
          onChange={(e) => setCourse({ ...course, title: e.target.value })}
        />

        <Label>Description</Label>
        <TextArea
          value={course.description}
          onChange={(e) =>
            setCourse({ ...course, description: e.target.value })
          }
        />

        <Label>Course Image</Label>
        <Input
          type="file"
          accept="image/*"
          onChange={(e) => setCourse({ ...course, image: e.target.files[0] })}
        />

        <Label>Demo Video</Label>
        <Input
          type="file"
          accept="video/*"
          onChange={(e) =>
            setCourse({ ...course, demoVideo: e.target.files[0] })
          }
        />

        <Label>Price (₹)</Label>
        <Input
          type="number"
          value={course.price}
          onChange={(e) => setCourse({ ...course, price: e.target.value })}
        />

        <Label>Offer Discount (%)</Label>
        <Input
          type="number"
          value={course.discount}
          onChange={(e) => setCourse({ ...course, discount: e.target.value })}
        />

        {/* 👇 Final Price Display */}
        {course.price && (
          <p>
            Final Price After Discount:{" "}
            <strong style={{ color: "#00d4ff" }}>₹{finalPrice}</strong>
          </p>
        )}

        <Label>Category</Label>
        <Input
          type="text"
          value={course.category}
          onChange={(e) => setCourse({ ...course, category: e.target.value })}
        />

        <Label>Tutor</Label>
        <Input
          type="text"
          value={course.tutor}
          onChange={(e) => setCourse({ ...course, tutor: e.target.value })}
        />

        {/* 👇 Coming Date */}
        <Label>Coming / Release Date</Label>
        <Input
          type="date"
          value={course.comingDate}
          onChange={(e) => setCourse({ ...course, comingDate: e.target.value })}
        />

        <Title style={{ marginTop: "30px" }}>Chapters</Title>
        <Label>Chapter Title</Label>
        <Input
          type="text"
          value={newChapter.title}
          onChange={(e) =>
            setNewChapter({ ...newChapter, title: e.target.value })
          }
        />

        <Label>Duration (optional)</Label>
        <Input
          type="text"
          placeholder="e.g. 15min"
          value={newChapter.duration}
          onChange={(e) =>
            setNewChapter({ ...newChapter, duration: e.target.value })
          }
        />

        <Label>Upload Video</Label>
        <Input
          type="file"
          accept="video/*"
          onChange={(e) =>
            setNewChapter({ ...newChapter, video: e.target.files[0] })
          }
        />

        <Button type="button" onClick={handleAddChapter}>
          <Plus size={18} /> Add Chapter
        </Button>

        <ChapterList>
          {chapters.map((chap, index) => (
            <ChapterItem key={index}>
              <span>
                {chap.title}
                {chap.duration ? ` (${chap.duration})` : ""}
              </span>
              <Trash
                size={18}
                style={{ cursor: "pointer", color: "red" }}
                onClick={() => handleRemoveChapter(index)}
              />
            </ChapterItem>
          ))}
        </ChapterList>

        <Button onClick={handleUpload} disabled={loading}>
          {loading ? (
            "Uploading..."
          ) : (
            <>
              <Save size={18} /> Save Course
            </>
          )}
        </Button>
      </Card>
    </PageContainer>
  );
};

export default AddCoursePage;
